import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import mammoth from 'mammoth';
import { useUser } from '../../contexts/UserContext';
import { generateInterviewQuestions } from '../../services/openaiService';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import FreeCreditsLabel from '../../components/FreeCreditsLabel';
import {
  createDoc,
  PDF,
  fR,
  sR,
  T,
  drawCoverHeader,
  drawPageHeader,
  drawFooters,
  drawMetaCard,
  drawSectionHeading,
} from '../../utils/pdfBrand';
import {
  HelpIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  SendIcon,
  XIcon,
  LinkIcon,
  ChevronUpIcon,
} from '../icons';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/* ─────────────────────────────────────────────────────────────────────────
 * Interview Questions Generator — Electron-native pane.
 *
 * Two views:
 *   1. SetupView   — job title, company, JD, resume (profile or upload).
 *                    Calls deduct-credit then generateInterviewQuestions
 *                    (same path the website uses).
 *   2. ResultsView — searchable, expandable Q&A list. Copy-all, download
 *                    branded PDF, generate-new.
 * ─────────────────────────────────────────────────────────────────────── */

async function extractText(file) {
  const name = (file.name || '').toLowerCase();
  if (file.type === 'text/plain' || name.endsWith('.txt')) return await file.text();
  if (file.type === 'application/pdf' || name.endsWith('.pdf')) {
    const buf = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    let out = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const c = await page.getTextContent();
      out += c.items.map(it => it.str).join(' ') + '\n';
    }
    return out.trim();
  }
  if (name.endsWith('.docx') || name.endsWith('.doc') || file.type.includes('word')) {
    const buf = await file.arrayBuffer();
    const r = await mammoth.extractRawText({ arrayBuffer: buf });
    return (r.value || '').trim();
  }
  throw new Error('Unsupported file. Use PDF, DOCX, DOC, or TXT.');
}

function buildResumeTextFromProfile(profile) {
  if (!profile) return '';
  let text = '';
  if (profile.fullName) text += `${profile.fullName}\n`;
  if (profile.jobTitle) text += `${profile.jobTitle}\n`;
  if (profile.location) text += `Location: ${profile.location}\n`;
  if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
  text += '\n';
  if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
  if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
  if (Array.isArray(profile.workExperience) && profile.workExperience.length) {
    text += 'WORK EXPERIENCE\n';
    for (const exp of profile.workExperience) {
      if (exp.position || exp.company) {
        text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
        if (exp.description) text += `  ${exp.description}\n`;
      }
    }
    text += '\n';
  }
  if (Array.isArray(profile.education) && profile.education.length) {
    text += 'EDUCATION\n';
    for (const ed of profile.education) {
      text += `${ed.degree || ''}${ed.institution || ed.school ? ` — ${ed.institution || ed.school}` : ''}${ed.year ? ` (${ed.year})` : ''}\n`;
    }
  }
  return text.trim();
}

export default function InterviewQuestionsPane() {
  const [view, setView] = useState('setup'); // 'setup' | 'results'
  const [meta, setMeta] = useState(null);    // { jobTitle, companyName }
  const [questions, setQuestions] = useState([]);

  if (view === 'setup') {
    return (
      <SetupView
        onGenerated={(metaData, qs) => {
          setMeta(metaData);
          setQuestions(qs);
          setView('results');
        }}
      />
    );
  }
  return (
    <ResultsView
      meta={meta}
      questions={questions}
      onReset={() => {
        setView('setup');
        setMeta(null);
        setQuestions([]);
      }}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* SetupView                                                               */
/* ─────────────────────────────────────────────────────────────────────── */

function SetupView({ onGenerated }) {
  const { fetchCredits, setCredits } = useUser();

  const [form, setForm] = useState({ jobTitle: '', companyName: '', jobDescription: '' });
  const [resumeChoice, setResumeChoice] = useState('profile');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedText, setUploadedText] = useState('');
  const [parseError, setParseError] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [profile, setProfile] = useState({ data: null, hasResume: false, loading: true });
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef(null);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (cancelled) return;
        const profileObj = d?.profile;
        const filename = profileObj?.resumeFilename || null;
        setProfile({ data: profileObj, hasResume: !!filename, loading: false });
        if (!filename) setResumeChoice('upload');
      } catch {
        if (!cancelled) setProfile({ data: null, hasResume: false, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setParseError('');
    setIsParsing(true);
    try {
      const text = await extractText(file);
      if (!text) throw new Error('No text extracted from file.');
      setUploadedText(text);
    } catch (err) {
      setParseError(err.message || 'Failed to parse file.');
      setUploadedText('');
    } finally {
      setIsParsing(false);
    }
  };

  const canStart = (() => {
    if (!form.jobTitle.trim() || !form.jobDescription.trim()) return false;
    if (resumeChoice === 'upload' && !uploadedText) return false;
    if (resumeChoice === 'profile' && !profile.hasResume) return false;
    return true;
  })();

  const gate = useFeatureGate('interviewQuestions');

  const handleGenerate = async () => {
    if (!canStart) return;
    if (gate.locked) { gate.openPaywall(); return; }
    setGenerating(true);
    setError('');
    try {
      // 1) deduct credit via the shared gate — updates the per-feature
      //    counter chip and opens the paywall automatically on 403.
      const { ok, reason, remaining } = await gate.deduct();
      if (!ok) {
        if (reason === 'no-credits') { setGenerating(false); return; }
        throw new Error(reason || 'Failed to deduct credit');
      }
      if (setCredits && typeof remaining === 'number' && Number.isFinite(remaining)) {
        setCredits(prev => Math.max(0, (prev || 0) - 1));
      }

      const resumeText = resumeChoice === 'upload'
        ? uploadedText
        : buildResumeTextFromProfile(profile.data);

      // 2) generate (calls server-side aiComplete via openaiService)
      const qs = await generateInterviewQuestions(
        resumeText,
        form.jobDescription.trim(),
        form.jobTitle.trim(),
        form.companyName.trim(),
      );

      if (!Array.isArray(qs) || qs.length === 0) {
        throw new Error('The AI returned no questions. Please try again.');
      }

      // 3) save to DB (best-effort, non-blocking — same as website)
      fetch(`${API_BASE_URL}/api/interview-questions/save`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jobTitle: form.jobTitle.trim(),
          companyName: form.companyName.trim(),
          jobDescription: form.jobDescription.trim(),
          questions: qs,
          resumeFilename: uploadedFile?.name || profile.data?.resumeFilename || '',
        }),
      }).catch(() => { /* swallow — saving is non-essential */ });

      if (fetchCredits) fetchCredits();
      onGenerated(
        {
          jobTitle: form.jobTitle.trim(),
          companyName: form.companyName.trim(),
          jobDescription: form.jobDescription.trim(),
        },
        qs,
      );
    } catch (err) {
      setError(err.message || 'Failed to generate questions.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="pane">
      <div className="pane-eyebrow">
        <HelpIcon size={11} /> Interview Prep
      </div>
      <h1 className="pane-title">Interview Questions Generator</h1>

      <div className="pane-card">
        <div className="pane-row">
          <div className="pane-field">
            <label className="pane-label">Job title *</label>
            <input
              className="pane-input"
              placeholder="e.g. Senior Software Engineer"
              value={form.jobTitle}
              onChange={update('jobTitle')}
            />
          </div>
          <div className="pane-field">
            <label className="pane-label">Company</label>
            <input
              className="pane-input"
              placeholder="e.g. Twilio"
              value={form.companyName}
              onChange={update('companyName')}
            />
          </div>
        </div>

        <div className="pane-field">
          <label className="pane-label">Job description *</label>
          <textarea
            className="pane-textarea"
            placeholder="Paste the JD. The more detail, the more targeted the questions."
            value={form.jobDescription}
            onChange={update('jobDescription')}
          />
        </div>

        <div className="pane-field">
          <label className="pane-label">Resume *</label>
          {/* Side-by-side cards in a 2-col grid to save vertical space —
              matches the AI Interview Assistant + Mock Interview panes. */}
          <div className="es-resume-choices">
            <ResumeChoiceCard
              checked={resumeChoice === 'profile'}
              disabled={!profile.hasResume}
              onClick={() => profile.hasResume && setResumeChoice('profile')}
              label="Use my profile resume"
              status={
                profile.loading ? <span style={{ color: 'var(--es-text-subtle)' }}>(checking…)</span>
                : profile.hasResume ? (
                  <span className="es-choice-status ok"><CheckCircleIcon size={12} /> {profile.data?.resumeFilename}</span>
                ) : (
                  <span className="es-choice-status warn"><AlertTriangleIcon size={12} /> no resume on profile</span>
                )
              }
            />
            <ResumeChoiceCard
              checked={resumeChoice === 'upload'}
              onClick={() => setResumeChoice('upload')}
              label="Upload a different resume"
              status={uploadedFile && (
                <span className="es-choice-status ok">
                  <CheckCircleIcon size={12} /> {uploadedFile.name}
                  {isParsing && <span style={{ color: 'var(--es-text-subtle)', marginLeft: 6 }}>parsing…</span>}
                </span>
              )}
              action={resumeChoice === 'upload' && (
                <button type="button" className="es-choice-action" onClick={() => fileRef.current?.click()}>
                  {uploadedFile ? 'Replace file' : 'Choose file'}
                </button>
              )}
            />
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" onChange={onFile} style={{ display: 'none' }} />
          {parseError && <div className="pane-error">{parseError}</div>}
        </div>

        {error && <div className="pane-error">{error}</div>}
      </div>

      <button
        type="button"
        className="pane-cta"
        onClick={handleGenerate}
        disabled={!canStart || generating || gate.locked}
        title={gate.locked ? 'Out of free credits — upgrade to keep using' : ''}
      >
        {generating
          ? 'Generating questions…'
          : <>Generate Questions <SendIcon size={13} /> <FreeCreditsLabel feature="interviewQuestions" /></>}
      </button>
    </div>
  );
}

function ResumeChoiceCard({ checked, disabled, onClick, label, status, action }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={`es-choice ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <span className="es-choice-radio" aria-hidden="true">
        <span className="es-choice-radio-dot" />
      </span>
      <div className="es-choice-body">
        <div className="es-choice-label">{label}</div>
        {status && <div className="es-choice-meta">{status}</div>}
        {action && <div className="es-choice-actions">{action}</div>}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* ResultsView                                                             */
/* ─────────────────────────────────────────────────────────────────────── */

function ResultsView({ meta, questions, onReset }) {
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 1800);
  };

  const toggle = (i) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(i)) n.delete(i); else n.add(i);
      return n;
    });
  };
  const expandAll  = () => setExpanded(new Set(questions.map((_, i) => i)));
  const collapseAll = () => setExpanded(new Set());

  // Filter — case-insensitive match on question or answer text.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return questions.map((qa, i) => ({ qa, i }));
    return questions
      .map((qa, i) => ({ qa, i }))
      .filter(({ qa }) =>
        (qa.question || '').toLowerCase().includes(q) ||
        (qa.answer   || '').toLowerCase().includes(q)
      );
  }, [questions, search]);

  const handleCopyAll = async () => {
    const text = questions
      .map((q, i) => `Question ${i + 1}: ${q.question}\n\nAnswer: ${q.answer}\n\n---\n\n`)
      .join('');
    try {
      await navigator.clipboard.writeText(text);
      showToast('Copied all questions to clipboard');
    } catch {
      showToast('Could not copy to clipboard');
    }
  };

  const handleCopyOne = async (qa, idx) => {
    try {
      await navigator.clipboard.writeText(`Q${idx + 1}: ${qa.question}\n\nA: ${qa.answer}`);
      showToast(`Copied Q${idx + 1}`);
    } catch {
      showToast('Could not copy to clipboard');
    }
  };

  const handleDownloadPDF = () => {
    try {
      const doc = createDoc();
      const { W, H, M, CW } = PDF;
      let y = 0;
      const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      fR(doc, 255, 255, 255, 0, 0, W, H);
      drawCoverHeader(doc, {
        reportTitle: 'Interview Questions & Answers',
        role: meta?.jobTitle || '',
        company: meta?.companyName || '',
        levelLabel: '',
        dateStr,
      });
      y = 62;

      y = drawMetaCard(doc, [
        { label: 'Position',        value: meta?.jobTitle || '—' },
        { label: 'Company',         value: meta?.companyName || '—' },
        { label: 'Total Questions', value: String(questions.length) },
        { label: 'Date',            value: dateStr },
      ], y) + 12;

      y = drawSectionHeading(doc, `Interview Questions  ·  ${questions.length} questions`, y);

      const newPage = () => {
        doc.addPage();
        fR(doc, 255, 255, 255, 0, 0, W, H);
        drawPageHeader(doc, meta?.jobTitle || '');
        y = 21;
      };
      const checkBreak = (need) => { if (y + need > H - 16) newPage(); };

      questions.forEach((q, index) => {
        const qLines = doc.splitTextToSize(q.question || '', CW - 20);
        const aLines = doc.splitTextToSize(q.answer   || '', CW - 14);
        const aBoxH = aLines.length * 5 + 13;
        checkBreak(qLines.length * 5 + aBoxH + 18);

        // Question header bar
        fR(doc, 22, 14, 52, M, y, CW, 10 + (qLines.length - 1) * 5);
        fR(doc, 109, 40, 217, M, y, 4, 10 + (qLines.length - 1) * 5);
        T(doc, [196, 181, 253], 8, true, `Q${String(index + 1).padStart(2, '0')}`, M + 7, y + 7);
        T(doc, [230, 225, 248], 8, false, qLines, M + 18, y + 7);
        y += 10 + (qLines.length - 1) * 5 + 3;

        // Answer card
        checkBreak(aBoxH);
        fR(doc, 246, 244, 255, M, y, CW, aBoxH);
        sR(doc, 218, 213, 238, 0.3, M, y, CW, aBoxH);
        fR(doc, 109, 40, 217, M, y, 3.5, aBoxH);
        T(doc, [76, 29, 149], 7, true, 'ANSWER', M + 7, y + 6);
        T(doc, [35, 28, 55], 8, false, aLines, M + 7, y + 12);
        y += aBoxH + 10;
      });

      drawFooters(doc);
      const fileName = `terview-questions-${(meta?.jobTitle || 'interview').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(fileName);
      showToast('PDF downloaded');
    } catch (err) {
      console.error('PDF export failed', err);
      showToast('Could not generate PDF');
    }
  };

  const allExpanded = expanded.size === questions.length && questions.length > 0;

  return (
    <div className="iqp-root">
      <div className="iqp-summary">
        <div className="iqp-summary-head">
          <div>
            <div className="pane-eyebrow">
              <HelpIcon size={11} /> Generated
            </div>
            <h1 className="pane-title" style={{ marginTop: 8 }}>
              {questions.length} Interview Questions
            </h1>
            <p className="pane-subtitle" style={{ marginBottom: 0 }}>
              {meta?.jobTitle}{meta?.companyName && ` · ${meta.companyName}`}
            </p>
          </div>
          <div className="iqp-summary-actions">
            <button type="button" className="pane-cta-secondary" onClick={onReset}>
              Generate new
            </button>
            <button type="button" className="pane-cta-secondary" onClick={handleCopyAll}>
              <LinkIcon size={13} /> Copy all
            </button>
            <button type="button" className="pane-cta" onClick={handleDownloadPDF}>
              <SendIcon size={13} /> Download PDF
            </button>
          </div>
        </div>

        <div className="iqp-controls">
          <div className="iqp-search">
            <input
              type="text"
              className="pane-input"
              placeholder="Search questions or answers…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="iqp-search-clear"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                <XIcon size={14} />
              </button>
            )}
          </div>
          <button
            type="button"
            className="iqp-toggle-all"
            onClick={allExpanded ? collapseAll : expandAll}
            disabled={questions.length === 0}
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        </div>

        <div className="iqp-meta-row">
          <span>{filtered.length} of {questions.length} shown</span>
          <span>·</span>
          <span>{expanded.size} expanded</span>
        </div>
      </div>

      <div className="iqp-list">
        {filtered.length === 0 ? (
          <div className="iqp-empty">
            <AlertTriangleIcon size={20} />
            <span>No questions match your search.</span>
          </div>
        ) : filtered.map(({ qa, i }) => (
          <QuestionRow
            key={i}
            index={i}
            qa={qa}
            isOpen={expanded.has(i)}
            onToggle={() => toggle(i)}
            onCopy={() => handleCopyOne(qa, i)}
          />
        ))}
      </div>

      {toast && <div className="iqp-toast">{toast}</div>}
    </div>
  );
}

function QuestionRow({ index, qa, isOpen, onToggle, onCopy }) {
  return (
    <div className={`iqp-row ${isOpen ? 'open' : ''}`}>
      <button type="button" className="iqp-row-head" onClick={onToggle}>
        <span className="iqp-row-num">Q{String(index + 1).padStart(2, '0')}</span>
        <span className="iqp-row-text">{qa.question}</span>
        <span className={`iqp-row-caret ${isOpen ? 'open' : ''}`}>
          <ChevronUpIcon size={14} />
        </span>
      </button>
      {isOpen && (
        <div className="iqp-row-body">
          <div className="iqp-row-answer-label">
            <span>Answer</span>
            <button
              type="button"
              className="iqp-row-copy"
              onClick={(e) => { e.stopPropagation(); onCopy(); }}
              title="Copy this question and answer"
            >
              <LinkIcon size={11} /> Copy
            </button>
          </div>
          <p className="iqp-row-answer">{qa.answer}</p>
        </div>
      )}
    </div>
  );
}
